// petconnect-v2/frontend/src/App.js
// MERGED: /post/:postId route (post detail) + /visual-search route (AI photo search)
import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Home, Search, MessageSquare, Users, Menu as MenuIcon, LogOut, User } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/common/Sidebar';
import { AuthForm } from './components/features/Auth';
import { Feed } from './components/features/Feed';
import { SearchPage, VisualSearchPage } from './components/features/Search';
import PublicSearchPage from './components/features/Search/PublicSearchPage';
import { MessagesPage } from './components/features/Messages';
import { FriendsPage } from './components/features/Friends';
import { MapPage } from './components/features/MapView';
import { UserProfile } from './components/features/Profile';
import AboutPage from './components/features/About/AboutPage';
import AdoptionPage from './components/features/Adoption/AdoptionPage';
import PetWalkerPage from './components/features/PetWalker/PetWalkerPage';
import GuestBanner from './components/common/GuestBanner';
import PostDetailPage from './components/features/Feed/PostDetailPage';

// ==========================================
// MOBILE BOTTOM NAVIGATION
// ==========================================
const MobileBottomNav = ({ onMenuClick, onProfileClick, onLogout, showMobileMenu, user, isAnonymous }) => {
  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-gray-200 flex justify-around items-center py-2 z-40 shadow-lg">
        <a href="/" className="flex flex-col items-center gap-1 py-2 px-3 flex-1 transition duration-200 text-gray-600 hover:text-purple-600">
          <Home size={24} />
          <span className="text-xs font-semibold">Feed</span>
        </a>
        <a href="/search" className="flex flex-col items-center gap-1 py-2 px-3 flex-1 transition duration-200 text-gray-600 hover:text-purple-600">
          <Search size={24} />
          <span className="text-xs font-semibold">Search</span>
        </a>
        <a href="/messages" className="flex flex-col items-center gap-1 py-2 px-3 flex-1 transition duration-200 text-gray-600 hover:text-purple-600">
          <MessageSquare size={24} />
          <span className="text-xs font-semibold">Chat</span>
        </a>
        <a href="/friends" className="flex flex-col items-center gap-1 py-2 px-3 flex-1 transition duration-200 text-gray-600 hover:text-purple-600">
          <Users size={24} />
          <span className="text-xs font-semibold">Friends</span>
        </a>
        <button
          onClick={onMenuClick}
          className={`flex flex-col items-center gap-1 py-2 px-3 flex-1 transition duration-200 ${showMobileMenu ? 'text-purple-600 bg-gray-100' : 'text-gray-600'}`}
        >
          <MenuIcon size={24} />
          <span className="text-xs font-semibold">More</span>
        </button>
      </nav>

      {showMobileMenu && (
        <div
          className="fixed inset-0 lg:hidden z-35 bg-black bg-opacity-20"
          onClick={onMenuClick}
          style={{ top: '0', bottom: '56px' }}
        />
      )}

      {showMobileMenu && (
        <div
          className="fixed left-0 right-0 bg-white border-t border-gray-200 lg:hidden z-40 max-h-80 overflow-y-auto shadow-2xl"
          style={{ bottom: '56px' }}
        >
          <div className="flex flex-col">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {isAnonymous ? '👤' : (user?.username?.[0]?.toUpperCase() || 'U')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-800 truncate">
                    {isAnonymous ? 'Guest User' : (user?.username || 'User')}
                  </p>
                  <p className="text-xs text-gray-500">{isAnonymous ? 'Browsing anonymously' : 'Tap options below'}</p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                {!isAnonymous && (
                  <button
                    onClick={() => { onProfileClick(); onMenuClick(); }}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition font-semibold text-sm"
                  >
                    <User size={16} />
                    Profile
                  </button>
                )}
                {isAnonymous && (
                  <a
                    href="/login"
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition font-semibold text-sm"
                  >
                    Sign In
                  </a>
                )}
                <button
                  onClick={onLogout}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-semibold text-sm"
                >
                  <LogOut size={16} />
                  {isAnonymous ? 'Exit' : 'Logout'}
                </button>
              </div>
            </div>

            <a href="/visual-search" onClick={onMenuClick} className="w-full text-left px-6 py-4 border-b border-gray-100 font-semibold text-base transition text-gray-700 hover:bg-gray-50">🤖 AI Photo Search</a>
            <a href="/petwalkers"    onClick={onMenuClick} className="w-full text-left px-6 py-4 border-b border-gray-100 font-semibold text-base transition text-gray-700 hover:bg-gray-50">🚶 Pet Walkers</a>
            <a href="/adoption"     onClick={onMenuClick} className="w-full text-left px-6 py-4 border-b border-gray-100 font-semibold text-base transition text-gray-700 hover:bg-gray-50">❤️ Adoption</a>
            <a href="/missing"      onClick={onMenuClick} className="w-full text-left px-6 py-4 border-b border-gray-100 font-semibold text-base transition text-gray-700 hover:bg-gray-50">🔴 Missing Pets</a>
            <a href="/found"        onClick={onMenuClick} className="w-full text-left px-6 py-4 border-b border-gray-100 font-semibold text-base transition text-gray-700 hover:bg-gray-50">🟢 Found Pets</a>
            <a href="/success"      onClick={onMenuClick} className="w-full text-left px-6 py-4 border-b border-gray-100 font-semibold text-base transition text-gray-700 hover:bg-gray-50">⭐ Success Stories</a>
            <a href="/map"          onClick={onMenuClick} className="w-full text-left px-6 py-4 border-b border-gray-100 font-semibold text-base transition text-gray-700 hover:bg-gray-50">🗺️ Map View</a>
            <a href="/about"        onClick={onMenuClick} className="w-full text-left px-6 py-4 font-semibold text-base transition text-gray-700 hover:bg-gray-50">ℹ️ About Us</a>
          </div>
        </div>
      )}
    </>
  );
};

// ==========================================
// GUEST RESTRICTED PAGE
// ==========================================
const GuestRestricted = ({ feature }) => (
  <div className="max-w-md mx-auto mt-12">
    <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
      <div className="text-5xl mb-4">🔒</div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">{feature} Requires an Account</h2>
      <p className="text-gray-500 mb-6 text-sm">
        Create a free account or login to access {feature.toLowerCase()} and connect with the community.
      </p>
      <div className="flex flex-col gap-3">
        <a
          href="/login"
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition shadow-md"
        >
          Login / Create Account
        </a>
        <a
          href="/"
          className="w-full flex items-center justify-center gap-2 px-6 py-2 text-gray-500 hover:text-gray-700 transition text-sm"
        >
          ← Back to Feed
        </a>
      </div>
    </div>
  </div>
);

// ==========================================
// MAIN APP CONTENT
// ==========================================
const AppContent = () => {
  const { user, loading, logout, isAnonymous } = useAuth();
  const [showProfile, setShowProfile]       = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500">
        <div className="text-3xl text-white font-bold animate-pulse">🐾 Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login"           element={<AuthForm />} />
        {/* Post detail is publicly accessible — shareable links work without login */}
        <Route path="/post/:postId"    element={<PostDetailPage />} />
        <Route path="*"                element={<PublicSearchPage onLoginClick={() => window.location.href = '/login'} />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex">
      <Sidebar
        className="hidden lg:flex lg:fixed lg:left-0 lg:top-0 lg:w-64 lg:h-screen overflow-y-auto border-r border-gray-200 z-50"
        onProfileClick={() => !isAnonymous && setShowProfile(true)}
      />

      <main className="flex-1 lg:ml-64 p-3 sm:p-4 md:p-6 lg:p-8 pb-24 lg:pb-8 overflow-auto">
        {isAnonymous && <GuestBanner />}

        <div className="min-h-screen">
          <Routes>
            <Route path="/"              element={<Feed />} />
            <Route path="/search"        element={<SearchPage user={user} />} />
            {/* AI photo search */}
            <Route path="/visual-search" element={<VisualSearchPage />} />
            {/* Post detail — full-screen, own top-bar, no sidebar padding needed */}
            <Route path="/post/:postId"  element={<PostDetailPage />} />
            <Route
              path="/messages"
              element={isAnonymous ? <GuestRestricted feature="Messaging" /> : <MessagesPage user={user} />}
            />
            <Route
              path="/friends"
              element={isAnonymous ? <GuestRestricted feature="Friends" /> : <FriendsPage user={user} />}
            />
            <Route path="/petwalkers" element={<PetWalkerPage user={user} />} />
            <Route path="/adoption"   element={<AdoptionPage user={user} />} />
            <Route path="/missing"    element={<Feed />} />
            <Route path="/found"      element={<Feed />} />
            <Route path="/success"    element={<Feed />} />
            <Route path="/map"        element={<MapPage user={user} />} />
            <Route path="/about"      element={<AboutPage />} />
            <Route path="*"           element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>

      <MobileBottomNav
        onMenuClick={() => setShowMobileMenu(!showMobileMenu)}
        onProfileClick={() => setShowProfile(true)}
        onLogout={logout}
        showMobileMenu={showMobileMenu}
        user={user}
        isAnonymous={isAnonymous}
      />

      {showProfile && !isAnonymous && (
        <UserProfile user={user} onClose={() => setShowProfile(false)} />
      )}
    </div>
  );
};

// ==========================================
// ROOT
// ==========================================
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}